/**
 * The addresses the press prints.
 *
 * A QR code in the front of a book is the one URL the press can never change:
 * it is on paper, in someone's hands, and there is no redirect to hide behind.
 * So the path is built here, once, and both sides import it — the generator to
 * print it, and the site to answer it. `site/src/lib/routes.ts` used to own
 * `/help/<slug>` alone, which was safe only while nothing else knew the shape.
 *
 * Nothing here may import pdf-lib: the site reaches this file through `@gen`.
 */

export const SITE_ORIGIN = "https://wisehyena.com";

/**
 * A book's id, derived rather than assigned so the generator and the published
 * record cannot drift apart.
 *
 * Title plus *designation*, not title plus subtitle. The printed subtitle
 * carries flavour and a separator — "An Introduction · Warm-Up to Medium" —
 * which makes a long URL that reads like a mistake. The designation is the half
 * that actually distinguishes one book from another, and the press publishes
 * one book per format per band, so title + designation is unique.
 */
export function bookSlug(title: string, designation: string): string {
  return `${slugify(title)}-${slugify(designation)}`;
}

/** Lowercase, ASCII, dash-separated: anything else collapses to a single dash. */
export function slugify(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Where a book's help lives, rooted. Printed into the book; served by the site. */
export function helpPath(slug: string): string {
  return `/help/${slug}`;
}

/** The absolute form, which is what goes in the QR code. */
export function helpUrl(slug: string): string {
  return SITE_ORIGIN + helpPath(slug);
}
